import React, { useState, useEffect } from 'react';
import { 
  Star, 
  MessageSquare, 
  User, 
  Send,
  Eye,
  Shield,
  TrendingUp,
  X
} from 'lucide-react';
import apiService from '../services/api';
import Modal from '../components/Modal';

const AvisPlateforme = () => {
  const [avis, setAvis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAvis, setSelectedAvis] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReponseModal, setShowReponseModal] = useState(false);
  const [reponseText, setReponseText] = useState('');
  const [savingReponse, setSavingReponse] = useState(false);
  const [stats, setStats] = useState({ moyenne: 0, total: 0 });
  const [filter, setFilter] = useState('all'); // 'all', 'without-response', 'low-rating'

  useEffect(() => {
    loadAvis();
  }, [filter]);

  const loadAvis = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('⭐ Chargement avis plateforme');
      const response = await apiService.request('/avis/plateforme');
      console.log('⭐ Réponse API avis plateforme:', response);
      
      if (response && response.data) {
        let filteredAvis = Array.isArray(response.data) ? response.data : [];
        
        // Appliquer les filtres
        if (filter === 'without-response') {
          filteredAvis = filteredAvis.filter(a => !a.reponsePlateforme);
        } else if (filter === 'low-rating') {
          filteredAvis = filteredAvis.filter(a => a.note <= 2);
        }
        
        setAvis(filteredAvis);
        setStats(response.stats || { moyenne: 0, total: 0 });
      } else {
        setAvis([]);
        setStats({ moyenne: 0, total: 0 });
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des avis plateforme:', error);
      setError(`Erreur: ${error.response?.data?.error || error.message}`);
      setAvis([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRepondre = (avisItem) => {
    setSelectedAvis(avisItem);
    setReponseText(avisItem.reponsePlateforme || '');
    setShowReponseModal(true);
  };

  const handleSaveReponse = async () => {
    if (!selectedAvis || !reponseText.trim()) {
      alert('Veuillez saisir une réponse');
      return;
    }

    setSavingReponse(true);
    try {
      await apiService.request(`/avis/plateforme/${selectedAvis.id}/reponse`, {
        method: 'PUT',
        body: JSON.stringify({ reponse: reponseText.trim() })
      });
      
      alert('Réponse enregistrée avec succès !');
      setShowReponseModal(false);
      setReponseText('');
      setSelectedAvis(null);
      loadAvis(); // Recharger les avis
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde de la réponse:', error);
      alert(`Erreur: ${error.response?.data?.error || error.message}`);
    } finally {
      setSavingReponse(false);
    }
  };

  const renderStars = (note) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-5 w-5 ${
              star <= note
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 text-sm font-medium text-gray-700">{note}/5</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Chargement des avis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadAvis}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec statistiques */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <Shield className="h-8 w-8 text-purple-600" />
              <h3 className="text-2xl font-semibold text-gray-900">
                Avis sur Clickorico
              </h3>
            </div>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Star className="h-6 w-6 fill-yellow-400 text-yellow-400" />
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.moyenne.toFixed(1)}
                  </div>
                  <div className="text-sm text-gray-600">
                    {stats.total} avis{stats.total > 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-6 w-6 text-blue-600" />
                <div>
                  <div className="text-lg font-semibold text-gray-900">
                    {stats.total > 0 ? Math.round((stats.moyenne / 5) * 100) : 0}%
                  </div>
                  <div className="text-xs text-gray-600">Satisfaction</div>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={loadAvis}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md hover:bg-gray-50 border border-gray-300"
          >
            Actualiser
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Filtrer :</span>
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 text-sm rounded-md ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Tous ({stats.total})
          </button>
          <button
            onClick={() => setFilter('without-response')}
            className={`px-3 py-1 text-sm rounded-md ${
              filter === 'without-response'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Sans réponse
          </button>
          <button
            onClick={() => setFilter('low-rating')}
            className={`px-3 py-1 text-sm rounded-md ${
              filter === 'low-rating'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            ⚠️ Notes faibles (≤2)
          </button>
        </div>
      </div>

      {/* Liste des avis */}
      {avis.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Star className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Aucun avis pour le moment</p>
          <p className="text-gray-500 text-sm mt-2">
            Les avis des utilisateurs sur l'application Clickorico apparaîtront ici.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {avis.map((avisItem) => (
            <div
              key={avisItem.id}
              className={`bg-white border rounded-lg p-6 hover:shadow-md transition-shadow ${
                avisItem.note <= 2 ? 'border-red-200 bg-red-50' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* En-tête avis */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {avisItem.user?.avatar ? (
                        <img
                          src={avisItem.user.avatar}
                          alt={avisItem.user.nom}
                          className="h-10 w-10 rounded-full"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <User className="h-6 w-6 text-purple-600" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-gray-900">
                          {avisItem.user?.prenom} {avisItem.user?.nom}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(avisItem.createdAt)}
                        </div>
                      </div>
                    </div>
                    {renderStars(avisItem.note)}
                  </div>

                  {/* Commentaire */}
                  {avisItem.commentaire && (
                    <div className="mb-3">
                      <p className="text-gray-700">{avisItem.commentaire}</p>
                    </div>
                  )}

                  {/* Réponse Clickorico */}
                  {avisItem.reponsePlateforme && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-start space-x-2">
                        <div className="flex-shrink-0">
                          <Shield className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="flex-1 bg-purple-50 p-3 rounded-md">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-sm font-medium text-purple-900">
                              Réponse Clickorico
                            </span>
                            <span className="text-xs text-purple-600">
                              {formatDate(avisItem.dateReponse)}
                            </span>
                          </div>
                          <p className="text-gray-800 text-sm">{avisItem.reponsePlateforme}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="ml-4 flex flex-col space-y-2">
                  <button
                    onClick={() => {
                      setSelectedAvis(avisItem);
                      setShowDetailModal(true);
                    }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Voir les détails"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                  {!avisItem.reponsePlateforme && (
                    <button
                      onClick={() => handleRepondre(avisItem)}
                      className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
                      title="Répondre à l'avis"
                    >
                      <MessageSquare className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de détail */}
      {showDetailModal && selectedAvis && (
        <Modal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedAvis(null);
          }}
          title="Détail de l'avis"
        >
          <div className="space-y-4">
            <div className="flex items-center space-x-3 pb-4 border-b">
              {selectedAvis.user?.avatar ? (
                <img
                  src={selectedAvis.user.avatar}
                  alt={selectedAvis.user.nom}
                  className="h-12 w-12 rounded-full"
                />
              ) : (
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <User className="h-6 w-6 text-purple-600" />
                </div>
              )}
              <div>
                <div className="font-medium text-gray-900">
                  {selectedAvis.user?.prenom} {selectedAvis.user?.nom}
                </div>
                <div className="text-sm text-gray-500">
                  {formatDate(selectedAvis.createdAt)}
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Note</label>
              <div className="mt-1">{renderStars(selectedAvis.note)}</div>
            </div>

            {selectedAvis.commentaire && (
              <div>
                <label className="text-sm font-medium text-gray-700">Commentaire</label>
                <p className="mt-1 text-gray-700 bg-gray-50 p-3 rounded-md">
                  {selectedAvis.commentaire}
                </p>
              </div>
            )}

            {selectedAvis.reponsePlateforme && (
              <div>
                <label className="text-sm font-medium text-gray-700">Votre réponse</label>
                <p className="mt-1 text-gray-700 bg-purple-50 p-3 rounded-md">
                  {selectedAvis.reponsePlateforme}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Répondu le {formatDate(selectedAvis.dateReponse)}
                </p>
              </div>
            )}

            <div className="flex space-x-3 pt-4 border-t">
              {!selectedAvis.reponsePlateforme && (
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    handleRepondre(selectedAvis);
                  }}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center justify-center space-x-2"
                >
                  <MessageSquare className="h-4 w-4" />
                  <span>Répondre</span>
                </button>
              )}
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedAvis(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Fermer
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de réponse */}
      {showReponseModal && selectedAvis && (
        <Modal
          isOpen={showReponseModal}
          onClose={() => {
            setShowReponseModal(false);
            setReponseText('');
            setSelectedAvis(null);
          }}
          title="Répondre au nom de Clickorico"
        >
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="flex items-center space-x-2 mb-2">
                {renderStars(selectedAvis.note)}
              </div>
              {selectedAvis.commentaire && (
                <p className="text-gray-700 text-sm">{selectedAvis.commentaire}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Votre réponse (en tant que Clickorico)
              </label>
              <textarea
                value={reponseText}
                onChange={(e) => setReponseText(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Merci pour votre retour ! Nous apprécions vos commentaires et travaillons constamment à améliorer l'application..."
                maxLength={1000}
              />
              <p className="mt-1 text-xs text-gray-500">
                {reponseText.length} / 1000 caractères
              </p>
            </div>

            <div className="flex space-x-3 pt-4 border-t">
              <button
                onClick={handleSaveReponse}
                disabled={savingReponse || !reponseText.trim()}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {savingReponse ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Enregistrement...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Envoyer la réponse</span>
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowReponseModal(false);
                  setReponseText('');
                  setSelectedAvis(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Annuler
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AvisPlateforme;
















